import { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Alert,
  Snackbar,
  useTheme,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { homeDepotPartnerId } from '../../../common/utils/PartnerEnrollmentUtils';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import OptimusButton from '../../../common/library/Button/Button';
import { usePartnerConfigList } from '../Partner/Enrollment/usePartnerConfigList';

interface HDExistingAccModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (hasExistingAccount: boolean) => void;
}

export function HDExistingAccModal({ open, onClose, onSubmit }: HDExistingAccModalProps) {
  const partnerConfigList = usePartnerConfigList();
  const partnerLogoUrl = partnerConfigList.find((p) => p.id === homeDepotPartnerId)?.partnerLogoUrl;
  const theme = useTheme();
  const { t } = useTranslation();
  const { trackPartnerEnrollment } = useCustomEventTracking();
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);

  const options: Array<{ label: string; hasExistingAccount: boolean }> = [
    { label: t('homeDepot.enrollModal.yes'), hasExistingAccount: true },
    { label: t('homeDepot.enrollModal.no'), hasExistingAccount: false },
    { label: t('homeDepot.enrollModal.notSure'), hasExistingAccount: false },
  ];

  const handleSubmit = () => {
    if (selectedIndex === null) return;
    setLoading(true);
    const selected = options[selectedIndex];
    trackPartnerEnrollment('Submit', 'HomeDepotEnrollmentStarted', {
      proXtraAccountAnswer: selected.label,
    });
    onSubmit(selected.hasExistingAccount);
    setLoading(false);
    onClose();
  };

  return (
    <>
      <Snackbar
        open={openErrorSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>

      <Modal
        open={open}
        onClose={loading ? undefined : onClose}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box
          sx={{
            position: 'relative',
            backgroundColor: theme.palette.common.white,
            borderRadius: theme.spacing(1.5),
            width: theme.spacing(75),
            maxWidth: '90vw',
            outline: 'none',
            boxShadow: '0px 8px 10px -5px rgba(0,0,0,0.2), 0px 16px 24px 2px rgba(0,0,0,0.14), 0px 6px 30px 5px rgba(0,0,0,0.12)',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: theme.spacing(2),
              padding: theme.spacing(3),
              paddingRight: theme.spacing(6),
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            <Box
              sx={{
                width: theme.spacing(7.5),
                height: theme.spacing(7.5),
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: theme.spacing(1.25),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                overflow: 'hidden',
                boxShadow: '0px 1px 2px 0px rgba(10,13,18,0.05)',
              }}
            >
              {partnerLogoUrl && (
                <Box
                  component="img"
                  src={partnerLogoUrl}
                  alt="Home Depot Pro"
                  sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                color={theme.palette.grey[900]}
                sx={{
                  mb: theme.spacing(0.5),
                  [theme.breakpoints.down('sm')]: {
                    fontSize: theme.spacing(1.75),
                    lineHeight: theme.spacing(2.75),
                  },
                }}
              >
                {t('homeDepot.enrollModal.title')}
              </Typography>
              <Typography
                variant="body2"
                color={theme.palette.grey[600]}
                sx={{
                  [theme.breakpoints.down('sm')]: {
                    fontSize: theme.spacing(1.5),
                  },
                }}
              >
                {t('homeDepot.enrollModal.subtitle')}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              disabled={loading}
              size="small"
              sx={{ position: 'absolute', top: theme.spacing(2), right: theme.spacing(2) }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{
            padding: theme.spacing(3),
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
          }}>
            <Typography
              variant="body2"
              color={theme.palette.grey[700]}
              sx={{
                [theme.breakpoints.down('sm')]: {
                  fontSize: theme.spacing(1.5),
                },
              }}
            >
              {t('homeDepot.enrollModal.question')}
            </Typography>
            <Box sx={{ display: 'flex', gap: theme.spacing(1.25), pb: theme.spacing(2) }}>
              {options.map((opt, index) => {
                const selected = selectedIndex === index;
                return (
                  <Box
                    key={opt.label}
                    onClick={() => !loading && setSelectedIndex(index)}
                    sx={{
                      flex: 1,
                      padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
                      borderRadius: theme.spacing(1),
                      border: `1.5px solid ${selected ? theme.palette.brand.primary : theme.palette.grey[300]}`,
                      backgroundColor: selected ?
                        theme.palette.brand.surface :
                        theme.palette.common.white,
                      cursor: loading ? 'default' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'border-color 0.15s, background-color 0.15s',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: selected ? 600 : 400,
                        color: selected ? theme.palette.brand.primary : theme.palette.grey[700],
                        [theme.breakpoints.down('sm')]: {
                          fontSize: theme.spacing(1.5),
                        },
                      }}
                    >
                      {opt.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            <OptimusButton
              customVariant="primary"
              buttonText={loading ?
                <CircularProgress size={16} color="inherit" /> :
                t('homeDepot.enrollModal.submit') as string}
              disabled={selectedIndex === null || loading}
              onClick={handleSubmit}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
}
